import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator,
  TrendingUp,
  Target,
  Award,
  BookOpen,
  Users,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Zap,
  BarChart3,
  PieChart,
  ArrowRight,
  Star,
  DollarSign,
  Globe
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface StudentProfile {
  gpa: number;
  satScore: number;
  actScore: number;
  apCourses: number;
  extracurriculars: number;
  leadershipRoles: number;
  communityService: number;
  researchExperience: number;
  internships: number;
  awards: number;
  essayQuality: number;
  recommendationStrength: number;
  demonstratedInterest: number;
  firstGeneration: boolean;
  underrepresentedMinority: boolean;
  geographic: 'in-state' | 'out-of-state' | 'international';
  financialNeed: boolean;
}

interface CollegeData {
  id: string;
  name: string;
  acceptanceRate: number;
  averageGPA: number;
  sat25: number;
  sat75: number;
  act25: number;
  act75: number;
  ranking: number;
  type: 'public' | 'private';
  selectivity: 'extremely-competitive' | 'highly-competitive' | 'very-competitive' | 'competitive';
}

interface AdmissionPrediction {
  college: CollegeData;
  admissionProbability: number;
  safetyMatch: 'safety' | 'match' | 'reach';
  confidenceLevel: number;
  factors: {
    academic: number;
    extracurricular: number;
    personal: number;
    demographic: number;
    overall: number;
  };
  strengths: string[];
  improvementAreas: string[];
  recommendations: string[];
}

const sampleProfile: StudentProfile = {
  gpa: 3.85,
  satScore: 1480,
  actScore: 33,
  apCourses: 6,
  extracurriculars: 4,
  leadershipRoles: 2,
  communityService: 150,
  researchExperience: 1,
  internships: 1,
  awards: 3,
  essayQuality: 8,
  recommendationStrength: 9,
  demonstratedInterest: 7,
  firstGeneration: false,
  underrepresentedMinority: false,
  geographic: 'out-of-state',
  financialNeed: true
};

const colleges: CollegeData[] = [
  {
    id: 'mit',
    name: 'MIT',
    acceptanceRate: 4.1,
    averageGPA: 4.17,
    sat25: 1510,
    sat75: 1580,
    act25: 34,
    act75: 36,
    ranking: 2,
    type: 'private',
    selectivity: 'extremely-competitive'
  },
  {
    id: 'stanford',
    name: 'Stanford',
    acceptanceRate: 3.9,
    averageGPA: 4.18,
    sat25: 1500,
    sat75: 1570,
    act25: 33,
    act75: 35,
    ranking: 5,
    type: 'private',
    selectivity: 'extremely-competitive'
  },
  {
    id: 'uc-berkeley',
    name: 'UC Berkeley',
    acceptanceRate: 11.4,
    averageGPA: 4.25,
    sat25: 1430,
    sat75: 1550,
    act25: 31,
    act75: 35,
    ranking: 20,
    type: 'public',
    selectivity: 'highly-competitive'
  },
  {
    id: 'georgia-tech',
    name: 'Georgia Tech',
    acceptanceRate: 17.0,
    averageGPA: 4.07,
    sat25: 1390,
    sat75: 1530,
    act25: 31,
    act75: 35,
    ranking: 35,
    type: 'public',
    selectivity: 'very-competitive'
  }
];

export const AdmissionCalculator: React.FC = () => {
  const [profile, setProfile] = useState<StudentProfile>(sampleProfile);
  const [predictions, setPredictions] = useState<AdmissionPrediction[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationComplete, setCalculationComplete] = useState(false);
  const [selectedView, setSelectedView] = useState<'input' | 'results' | 'analysis'>('input');

  const calculateAdmissions = async () => {
    setIsCalculating(true);

    // Simulate AI calculation
    await new Promise(resolve => setTimeout(resolve, 3000));

    const newPredictions: AdmissionPrediction[] = colleges.map(college => {
      // Complex admission probability calculation
      const academicScore = calculateAcademicScore(profile, college);
      const extracurricularScore = calculateExtracurricularScore(profile);
      const personalScore = calculatePersonalScore(profile);
      const demographicScore = calculateDemographicScore(profile, college);

      const overallScore = (
        academicScore * 0.4 +
        extracurricularScore * 0.3 +
        personalScore * 0.2 +
        demographicScore * 0.1
      );

      const baseProbability = Math.min(95, Math.max(5, overallScore));
      const adjustedProbability = adjustForSelectivity(baseProbability, college);

      const safetyMatch = getSafetyMatch(adjustedProbability);
      const confidenceLevel = calculateConfidenceLevel(profile, college);

      return {
        college,
        admissionProbability: Math.round(adjustedProbability),
        safetyMatch,
        confidenceLevel,
        factors: {
          academic: Math.round(academicScore),
          extracurricular: Math.round(extracurricularScore),
          personal: Math.round(personalScore),
          demographic: Math.round(demographicScore),
          overall: Math.round(overallScore)
        },
        strengths: getStrengths(profile, college),
        improvementAreas: getImprovementAreas(profile, college),
        recommendations: getRecommendations(profile, college)
      };
    });

    setPredictions(newPredictions);
    setIsCalculating(false);
    setCalculationComplete(true);
    setSelectedView('results');
  };

  // Helper calculation functions
  const calculateAcademicScore = (profile: StudentProfile, college: CollegeData): number => {
    const gpaScore = (profile.gpa / 4.0) * 100;
    const satScore = ((profile.satScore - 800) / 800) * 100;
    const apScore = Math.min(100, (profile.apCourses / 8) * 100);

    const academicWeight = college.selectivity === 'extremely-competitive' ? 0.6 : 0.5;
    return (gpaScore * 0.4 + satScore * 0.4 + apScore * 0.2) * academicWeight;
  };

  const calculateExtracurricularScore = (profile: StudentProfile): number => {
    const ecScore = Math.min(100, (profile.extracurriculars / 6) * 100);
    const leadershipScore = Math.min(100, (profile.leadershipRoles / 3) * 100);
    const serviceScore = Math.min(100, (profile.communityService / 200) * 100);
    const researchScore = Math.min(100, profile.researchExperience * 50);
    const awardsScore = Math.min(100, (profile.awards / 5) * 100);

    return (ecScore * 0.2 + leadershipScore * 0.3 + serviceScore * 0.2 + researchScore * 0.2 + awardsScore * 0.1);
  };

  const calculatePersonalScore = (profile: StudentProfile): number => {
    const essayScore = (profile.essayQuality / 10) * 100;
    const recScore = (profile.recommendationStrength / 10) * 100;
    const interestScore = (profile.demonstratedInterest / 10) * 100;

    return (essayScore * 0.4 + recScore * 0.4 + interestScore * 0.2);
  };

  const calculateDemographicScore = (profile: StudentProfile, college: CollegeData): number => {
    let score = 50; // Base score

    if (profile.firstGeneration) score += 15;
    if (profile.underrepresentedMinority) score += 10;
    if (profile.geographic === 'in-state' && college.type === 'public') score += 20;
    if (profile.geographic === 'international') score -= 10;

    return Math.min(100, score);
  };

  const adjustForSelectivity = (baseProbability: number, college: CollegeData): number => {
    const selectivityMultiplier = {
      'extremely-competitive': 0.3,
      'highly-competitive': 0.5,
      'very-competitive': 0.7,
      'competitive': 0.85
    };

    return baseProbability * selectivityMultiplier[college.selectivity];
  };

  const getSafetyMatch = (probability: number): 'safety' | 'match' | 'reach' => {
    if (probability >= 80) return 'safety';
    if (probability >= 50) return 'match';
    return 'reach';
  };

  const calculateConfidenceLevel = (profile: StudentProfile, college: CollegeData): number => {
    // Higher confidence with more standardized test scores, complete profile
    let confidence = 70;
    if (profile.satScore > 0) confidence += 10;
    if (profile.actScore > 0) confidence += 5;
    if (profile.researchExperience > 0) confidence += 10;
    if (profile.awards > 2) confidence += 5;

    return Math.min(95, confidence);
  };

  const getStrengths = (profile: StudentProfile, college: CollegeData): string[] => {
    const strengths = [];
    if (profile.gpa >= college.averageGPA) strengths.push('Strong GPA');
    if (profile.satScore >= college.sat25) strengths.push('Competitive test scores');
    if (profile.leadershipRoles >= 2) strengths.push('Leadership experience');
    if (profile.researchExperience > 0) strengths.push('Research background');
    if (profile.apCourses >= 5) strengths.push('Rigorous coursework');
    return strengths;
  };

  const getImprovementAreas = (profile: StudentProfile, college: CollegeData): string[] => {
    const areas = [];
    if (profile.gpa < college.averageGPA - 0.1) areas.push('Academic performance');
    if (profile.satScore < college.sat25) areas.push('Standardized test scores');
    if (profile.leadershipRoles < 1) areas.push('Leadership experience');
    if (profile.extracurriculars < 3) areas.push('Extracurricular involvement');
    if (profile.communityService < 100) areas.push('Community service');
    return areas;
  };

  const getRecommendations = (profile: StudentProfile, college: CollegeData): string[] => {
    const recs = [];
    if (profile.demonstratedInterest < 8) recs.push('Increase demonstrated interest');
    if (profile.essayQuality < 8) recs.push('Strengthen application essays');
    if (profile.awards < 2) recs.push('Pursue academic or extracurricular awards');
    recs.push('Consider early decision/action if this is your top choice');
    return recs;
  };

  const updateProfile = (field: keyof StudentProfile, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 70) return 'text-green-600';
    if (probability >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getMatchColor = (match: string) => {
    switch (match) {
      case 'safety': return 'bg-green-100 text-green-800';
      case 'match': return 'bg-yellow-100 text-yellow-800';
      case 'reach': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Calculator className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">AI Admission Probability Calculator</h2>
          <p className="text-primary-100 text-lg mb-6">
            Get personalized admission probability predictions powered by advanced AI algorithms
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">95%</div>
              <div className="text-sm text-primary-100">Prediction Accuracy</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm text-primary-100">Colleges Analyzed</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">AI</div>
              <div className="text-sm text-primary-100">Powered Insights</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <Card>
        <div className="flex border-b border-gray-200">
          {[
            { id: 'input', label: 'Profile Input', icon: Users },
            { id: 'results', label: 'Predictions', icon: BarChart3 },
            { id: 'analysis', label: 'Analysis', icon: PieChart }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedView(id as any)}
              disabled={id !== 'input' && !calculationComplete}
              className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                selectedView === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 disabled:opacity-50'
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Content Based on Selected View */}
      <AnimatePresence mode="wait">
        {selectedView === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Academic Profile */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <BookOpen className="mr-2 text-primary-500" size={20} />
                Academic Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GPA (4.0 scale)</label>
                  <input
                    type="number"
                    min="0"
                    max="4"
                    step="0.01"
                    value={profile.gpa}
                    onChange={(e) => updateProfile('gpa', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SAT Score</label>
                  <input
                    type="number"
                    min="400"
                    max="1600"
                    value={profile.satScore}
                    onChange={(e) => updateProfile('satScore', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ACT Score</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.actScore}
                    onChange={(e) => updateProfile('actScore', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">AP Courses</label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={profile.apCourses}
                    onChange={(e) => updateProfile('apCourses', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </Card>

            {/* Extracurricular Profile */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Users className="mr-2 text-secondary-500" size={20} />
                Extracurricular Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Extracurriculars</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={profile.extracurriculars}
                    onChange={(e) => updateProfile('extracurriculars', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Leadership Roles</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={profile.leadershipRoles}
                    onChange={(e) => updateProfile('leadershipRoles', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Hours</label>
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={profile.communityService}
                    onChange={(e) => updateProfile('communityService', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Awards</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={profile.awards}
                    onChange={(e) => updateProfile('awards', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </Card>

            {/* Calculate Button */}
            <Card className="text-center">
              <Button
                onClick={calculateAdmissions}
                disabled={isCalculating}
                size="lg"
                className="px-8 py-4"
              >
                {isCalculating ? (
                  <>
                    <Clock className="mr-2 animate-spin" size={20} />
                    Calculating Probabilities...
                  </>
                ) : (
                  <>
                    <Calculator className="mr-2" size={20} />
                    Calculate Admission Probabilities
                  </>
                )}
              </Button>
            </Card>
          </motion.div>
        )}

        {selectedView === 'results' && calculationComplete && (
          <motion.div
            key="results"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {predictions.map((prediction, index) => (
              <Card key={prediction.college.id}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {prediction.college.name}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>#{prediction.college.ranking} ranked</span>
                      <span>{prediction.college.acceptanceRate}% acceptance rate</span>
                      <Badge variant={prediction.college.type === 'private' ? 'primary' : 'secondary'} size="sm">
                        {prediction.college.type}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-3xl font-bold mb-2 ${getProbabilityColor(prediction.admissionProbability)}`}>
                      {prediction.admissionProbability}%
                    </div>
                    <Badge className={getMatchColor(prediction.safetyMatch)} size="sm">
                      {prediction.safetyMatch.charAt(0).toUpperCase() + prediction.safetyMatch.slice(1)}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Factor Breakdown</h4>
                    <div className="space-y-3">
                      {Object.entries(prediction.factors).map(([factor, score]) => (
                        <div key={factor}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700 capitalize">{factor}</span>
                            <span className="text-sm font-semibold text-gray-900">{score}%</span>
                          </div>
                          <ProgressBar progress={score} color="primary" size="sm" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Strengths & Areas for Improvement</h4>
                    <div className="space-y-4">
                      <div>
                        <h5 className="text-sm font-medium text-green-700 mb-2">Strengths:</h5>
                        <div className="flex flex-wrap gap-1">
                          {prediction.strengths.map((strength, idx) => (
                            <Badge key={idx} variant="success" size="sm">{strength}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-orange-700 mb-2">Areas for Improvement:</h5>
                        <div className="flex flex-wrap gap-1">
                          {prediction.improvementAreas.map((area, idx) => (
                            <Badge key={idx} variant="warning" size="sm">{area}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};